import { useAtom } from '@reatom/npm-react'
import { motion, useMotionValue, animate } from 'motion/react'
import { useCallback, useState } from 'react'
import styled from 'styled-components'

import { walletsModelAtom } from '@root/entities/wallet'

import {
  NOTIFICATION_WIDGET_WALLETS_ITEM_HEIGHT,
  NotificationWidgetWalletsItem,
} from './notification-widget-wallets-item'

const GAP = 4
const SLIDE_HEIGHT = NOTIFICATION_WIDGET_WALLETS_ITEM_HEIGHT + GAP
const VISIBLE_COUNT = 2
const VIEWPORT_HEIGHT = VISIBLE_COUNT * SLIDE_HEIGHT
const DRAG_THRESHOLD = SLIDE_HEIGHT / 3

export const NotificationWidgetWalletsBody: React.FC = () => {
  const [walletsModel] = useAtom(walletsModelAtom)
  const [wallets] = useAtom(walletsModel.wallets)

  const [index, setIndex] = useState(0)
  const y = useMotionValue(0)
  const canDrag = wallets.length >= 3
  const maxIndex = Math.max(0, wallets.length - VISIBLE_COUNT)

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(maxIndex, next))
      setIndex(clamped)
      animate(y, -clamped * SLIDE_HEIGHT, {
        type: 'spring',
        stiffness: 500,
        damping: 40,
      })
    },
    [maxIndex, y],
  )

  const handleDragEnd = (
    _: unknown,
    info: { offset: { y: number }; velocity: { y: number } },
  ) => {
    const { offset, velocity } = info
    if (offset.y < -DRAG_THRESHOLD || velocity.y < -500) {
      goTo(index + 1)
    } else if (offset.y > DRAG_THRESHOLD || velocity.y > 500) {
      goTo(index - 1)
    } else {
      goTo(index)
    }
  }

  return (
    <Root>
      <Viewport>
        <Track
          data-can-drag={canDrag || undefined}
          style={canDrag ? { y } : undefined}
          drag={canDrag ? 'y' : false}
          dragConstraints={
            canDrag
              ? {
                  top: -maxIndex * SLIDE_HEIGHT,
                  bottom: 0,
                }
              : undefined
          }
          dragElastic={canDrag ? 0.15 : 0}
          onDragEnd={canDrag ? handleDragEnd : undefined}
        >
          {wallets.map((wallet) => (
            <NotificationWidgetWalletsItem key={wallet.id} wallet={wallet} />
          ))}
        </Track>
      </Viewport>
    </Root>
  )
}

const Root = styled.div`
  border-radius: 8px;
`

const Viewport = styled.div`
  height: ${VIEWPORT_HEIGHT}px;
  overflow: hidden;
  touch-action: pan-x;

  --fade: 10px;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    var(--appearance-1) var(--fade),
    var(--appearance-1) calc(100% - var(--fade)),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    var(--appearance-1) var(--fade),
    var(--appearance-1) calc(100% - var(--fade)),
    transparent 100%
  );
`

const Track = styled(motion.div)`
  display: grid;
  grid-auto-rows: ${SLIDE_HEIGHT}px;

  &[data-can-drag] {
    cursor: grab;

    &:active {
      cursor: grabbing;
    }
  }
`
